import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ArrowRight,
  ArrowLeft,
  ArrowLeftRight,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Save,
  ChevronRight,
  Settings2,
  Layers,
  X,
  PenLine,
  ChevronsUpDown,
  Check,
  Search,
  Download,
  FileSpreadsheet,
  FileText,
  KeyRound,
  Link2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandScrollList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import * as XLSX from "xlsx";

// Build backend URL: use env var if set (add https:// if missing), fallback to same origin
let BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (BACKEND_URL && !BACKEND_URL.startsWith('http')) {
  BACKEND_URL = `https://${BACKEND_URL}`;
}
if (!BACKEND_URL) {
  BACKEND_URL = window.location.origin;
}
const API = `${BACKEND_URL}/api`;

// Debug
console.log('FieldMapping: BACKEND_URL =', BACKEND_URL);
console.log('FieldMapping: API =', API);

const directionOptions = [
  { value: "hubspot_to_bc", label: "HubSpot → BC", icon: ArrowRight },
  { value: "bc_to_hubspot", label: "BC → HubSpot", icon: ArrowLeft },
  { value: "bidirectional", label: "Bidirectionnel", icon: ArrowLeftRight },
];

export default function FieldMapping() {
  // State for objects
  const [hubspotObjects, setHubspotObjects] = useState([]);
  const [bcEntities, setBcEntities] = useState([]);
  const [objectMappings, setObjectMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for selected object mapping
  const [selectedObjectMapping, setSelectedObjectMapping] = useState(null);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [hubspotFields, setHubspotFields] = useState([]);
  const [bcFields, setBcFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  
  // Custom field mode (manual entry) and search
  const [bcCustomMode, setBcCustomMode] = useState(false);
  const [bcFieldsError, setBcFieldsError] = useState(null);
  const [bcComboboxOpen, setBcComboboxOpen] = useState(false);
  const [hsComboboxOpen, setHsComboboxOpen] = useState(false);

  // OData effective fields (mapping effectif)
  const [bcFieldTab, setBcFieldTab] = useState("effective"); // "effective" or "all"
  const [odataFields, setOdataFields] = useState(null); // { has_odata_support, odata_endpoint, fields, message }
  const [refreshingLabels, setRefreshingLabels] = useState(false);

  // Dialogs
  const [showObjectMappingDialog, setShowObjectMappingDialog] = useState(false);
  const [showFieldMappingDialog, setShowFieldMappingDialog] = useState(false);
  
  // Matching key configuration
  const [matchingKeyHs, setMatchingKeyHs] = useState("");
  const [matchingKeyBc, setMatchingKeyBc] = useState("");
  const [syncMode, setSyncMode] = useState("update_only");  // Safe default
  const [savingMatchingKeys, setSavingMatchingKeys] = useState(false);

  // New object mapping form
  const [newObjectMapping, setNewObjectMapping] = useState({
    hubspot_object: "",
    hubspot_object_label: "",
    bc_entity: "",
    bc_entity_label: "",
  });
  
  // New field mapping form
  const [newFieldMapping, setNewFieldMapping] = useState({
    hubspot_field: "",
    hubspot_field_label: "",
    bc_field: "",
    bc_field_label: "",
    sync_direction: "bidirectional",
    sync_mode: "inherit",
  });

  // Fetch available objects on mount
  useEffect(() => {
    fetchAvailableObjects();
    fetchObjectMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAvailableObjects = async () => {
    try {
      const [hsResponse, bcResponse] = await Promise.all([
        axios.get(`${API}/hubspot/objects`).catch(() => ({ data: [] })),
        axios.get(`${API}/bc/entities`).catch(() => ({ data: [] })),
      ]);
      setHubspotObjects(Array.isArray(hsResponse.data) ? hsResponse.data : []);
      setBcEntities(Array.isArray(bcResponse.data) ? bcResponse.data : []);
    } catch (error) {
      console.error("Failed to fetch objects:", error);
      setHubspotObjects([]);
      setBcEntities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchObjectMappings = async () => {
    try {
      const response = await axios.get(`${API}/object-mappings`);
      const mappings = Array.isArray(response.data) ? response.data : [];
      setObjectMappings(mappings);
      // Auto-select first mapping if none selected
      if (mappings.length > 0 && !selectedObjectMapping) {
        setSelectedObjectMapping(mappings[0]);
        setMatchingKeyHs(mappings[0].matching_key_hs || "");
        setMatchingKeyBc(mappings[0].matching_key_bc || "");
        setSyncMode(mappings[0].sync_mode || "create_and_update");
        fetchFieldsForObjectMapping(mappings[0]);
      }
    } catch (error) {
      console.error("Failed to fetch object mappings:", error);
      setObjectMappings([]);
    }
  };

  const fetchFieldsForObjectMapping = useCallback(async (objectMapping) => {
    setFieldsLoading(true);
    setBcFieldsError(null);
    setOdataFields(null);
    try {
      const [hsResponse, bcResponse, mappingsResponse, odataResponse] = await Promise.all([
        axios.get(`${API}/hubspot/properties/${objectMapping.hubspot_object}`).catch((err) => {
          console.error("HubSpot fields error:", err);
          return { data: [] };
        }),
        axios.get(`${API}/bc/properties/${objectMapping.bc_entity}`).catch((err) => {
          console.error("BC fields error:", err);
          const msg = err.response?.data?.detail || err.message || "Erreur inconnue";
          setBcFieldsError(msg);
          return { data: [], _error: true };
        }),
        axios.get(`${API}/mappings?object_mapping_id=${objectMapping.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/bc/odata-fields/${objectMapping.bc_entity}`).catch((err) => {
          console.error("OData fields error:", err);
          return { data: { has_odata_support: false, fields: [] } };
        }),
      ]);
      setHubspotFields(Array.isArray(hsResponse.data) ? hsResponse.data : []);
      const bcData = Array.isArray(bcResponse.data) ? bcResponse.data : [];
      setBcFields(bcData);
      if (bcData.length === 0 && !bcResponse._error) {
        setBcFieldsError("Aucun champ BC retourné. Vérifiez la connexion Business Central dans les paramètres.");
      }
      setFieldMappings(Array.isArray(mappingsResponse.data) ? mappingsResponse.data : []);
      setOdataFields(odataResponse.data);
    } catch (error) {
      console.error("Failed to fetch fields:", error);
      toast.error("Erreur lors du chargement des champs");
      setHubspotFields([]);
      setBcFields([]);
      setFieldMappings([]);
      setOdataFields(null);
    } finally {
      setFieldsLoading(false);
    }
  }, []);

  const handleSelectObjectMapping = (objectMapping) => {
    setSelectedObjectMapping(objectMapping);
    setMatchingKeyHs(objectMapping.matching_key_hs || "");
    setMatchingKeyBc(objectMapping.matching_key_bc || "");
    setSyncMode(objectMapping.sync_mode || "create_and_update");
    fetchFieldsForObjectMapping(objectMapping);
  };

  const handleSaveMatchingKeys = async () => {
    if (!selectedObjectMapping) return;
    setSavingMatchingKeys(true);
    try {
      const response = await axios.put(
        `${API}/object-mappings/${selectedObjectMapping.id}`,
        { matching_key_hs: matchingKeyHs, matching_key_bc: matchingKeyBc, sync_mode: syncMode }
      );
      setSelectedObjectMapping(response.data);
      // Update in the list too
      setObjectMappings(prev =>
        prev.map(m => m.id === response.data.id ? response.data : m)
      );
      toast.success("Configuration sauvegardée");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingMatchingKeys(false);
    }
  };

  const handleCreateObjectMapping = async () => {
    if (!newObjectMapping.hubspot_object || !newObjectMapping.bc_entity) {
      toast.error("Veuillez sélectionner un objet des deux côtés");
      return;
    }

    try {
      await axios.post(`${API}/object-mappings`, newObjectMapping);
      toast.success("Mapping d'objets créé avec succès");
      setShowObjectMappingDialog(false);
      setNewObjectMapping({
        hubspot_object: "",
        hubspot_object_label: "",
        bc_entity: "",
        bc_entity_label: "",
      });
      await fetchObjectMappings();
    } catch (error) {
      toast.error("Erreur lors de la création du mapping");
    }
  };

  const handleDeleteObjectMapping = async (mappingId) => {
    try {
      await axios.delete(`${API}/object-mappings/${mappingId}`);
      toast.success("Mapping d'objets supprimé");
      if (selectedObjectMapping?.id === mappingId) {
        setSelectedObjectMapping(null);
        setFieldMappings([]);
      }
      await fetchObjectMappings();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleCreateFieldMapping = async () => {
    if (!newFieldMapping.hubspot_field || !newFieldMapping.bc_field) {
      toast.error("Veuillez sélectionner ou saisir les deux champs");
      return;
    }

    const hsField = hubspotFields.find(f => f.name === newFieldMapping.hubspot_field);
    const bcField = bcFields.find(f => f.name === newFieldMapping.bc_field);

    // For custom fields, use the label if provided or generate from field name
    const bcLabel = bcField?.label
      || newFieldMapping.bc_field_label
      || newFieldMapping.bc_field;

    try {
      await axios.post(`${API}/mappings`, {
        object_mapping_id: selectedObjectMapping.id,
        hubspot_field: newFieldMapping.hubspot_field,
        hubspot_field_label: hsField?.label || newFieldMapping.hubspot_field,
        bc_field: newFieldMapping.bc_field,
        bc_field_label: bcLabel,
        sync_direction: newFieldMapping.sync_direction,
        is_active: true,
      });

      toast.success("Mapping de champ créé avec succès");
      setShowFieldMappingDialog(false);
      setBcCustomMode(false);
      setNewFieldMapping({
        hubspot_field: "",
        hubspot_field_label: "",
        bc_field: "",
        bc_field_label: "",
        sync_direction: "bidirectional",
      });
      await fetchFieldsForObjectMapping(selectedObjectMapping);
    } catch (error) {
      toast.error("Erreur lors de la création du mapping");
    }
  };

  const handleDeleteFieldMapping = async (mappingId) => {
    try {
      await axios.delete(`${API}/mappings/${mappingId}`);
      toast.success("Mapping supprimé");
      await fetchFieldsForObjectMapping(selectedObjectMapping);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleFieldActive = async (mapping) => {
    try {
      await axios.put(`${API}/mappings/${mapping.id}`, {
        ...mapping,
        is_active: !mapping.is_active,
      });
      await fetchFieldsForObjectMapping(selectedObjectMapping);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDirectionChange = async (mapping, newDirection) => {
    try {
      await axios.put(`${API}/mappings/${mapping.id}`, {
        ...mapping,
        sync_direction: newDirection,
      });
      toast.success("Direction mise à jour");
      await fetchFieldsForObjectMapping(selectedObjectMapping);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleSyncModeChange = async (mapping, newMode) => {
    try {
      await axios.put(`${API}/mappings/${mapping.id}`, {
        ...mapping,
        sync_mode: newMode,
      });
      toast.success("Mode de sync mis à jour");
      await fetchFieldsForObjectMapping(selectedObjectMapping);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleRefreshLabels = async () => {
    if (!selectedObjectMapping) return;
    setRefreshingLabels(true);
    try {
      const res = await axios.post(`${API}/mappings/refresh-labels`, {
        object_mapping_id: selectedObjectMapping.id,
      });
      const { updated, changes } = res.data;
      if (updated > 0) {
        const details = changes.map((c) =>
          c.bc_field_old !== c.bc_field_new
            ? `${c.bc_field_old} → ${c.bc_field_new}`
            : `${c.bc_field_new}: ${c.old_label} → ${c.new_label}`
        ).join(", ");
        toast.success(`${updated} champ(s) mis à jour: ${details}`);
      } else {
        toast.info("Tous les champs sont déjà à jour");
      }
      await fetchFieldsForObjectMapping(selectedObjectMapping);
    } catch (error) {
      toast.error("Erreur lors du rafraîchissement des labels");
    } finally {
      setRefreshingLabels(false);
    }
  };

  const DirectionIcon = ({ direction, className = "" }) => {
    const option = directionOptions.find(d => d.value === direction);
    if (!option) return null;
    const Icon = option.icon;
    return <Icon className={className} />;
  };

  // --- Export helpers ---
  const fieldsToRows = (fields, source) =>
    fields.map((f) => ({
      Source: source,
      "Nom technique": f.name,
      Label: f.label || f.name,
      Type: f.type || "",
    }));

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCSV = (fields, source) => {
    const rows = fieldsToRows(fields, source);
    if (rows.length === 0) {
      toast.error(`Aucun champ ${source} à exporter`);
      return;
    }
    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(";"),
      ...rows.map((r) => headers.map((h) => `"${(r[h] || "").replace(/"/g, '""')}"`).join(";")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const entityLabel = selectedObjectMapping?.bc_entity_label || selectedObjectMapping?.bc_entity || "fields";
    downloadBlob(blob, `${source}_${entityLabel}_champs.csv`);
    toast.success(`${rows.length} champs ${source} exportés en CSV`);
  };

  const exportExcel = (mode) => {
    const wb = XLSX.utils.book_new();
    const entityLabel = selectedObjectMapping?.bc_entity_label || selectedObjectMapping?.bc_entity || "fields";

    if (mode === "hubspot" || mode === "all") {
      const hsRows = fieldsToRows(hubspotFields, "HubSpot");
      if (hsRows.length > 0) {
        const ws = XLSX.utils.json_to_sheet(hsRows);
        ws["!cols"] = [{ wch: 10 }, { wch: 35 }, { wch: 35 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, ws, "HubSpot");
      }
    }
    if (mode === "bc" || mode === "all") {
      const bcRows = fieldsToRows(bcFields, "Business Central");
      if (bcRows.length > 0) {
        const ws = XLSX.utils.json_to_sheet(bcRows);
        ws["!cols"] = [{ wch: 18 }, { wch: 35 }, { wch: 35 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, ws, "Business Central");
      }
    }

    if (wb.SheetNames.length === 0) {
      toast.error("Aucun champ à exporter");
      return;
    }

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const prefix = mode === "all" ? "HubSpot_BC" : mode === "hubspot" ? "HubSpot" : "BC";
    downloadBlob(blob, `${prefix}_${entityLabel}_champs.xlsx`);
    const total = (mode === "hubspot" || mode === "all" ? hubspotFields.length : 0) +
                  (mode === "bc" || mode === "all" ? bcFields.length : 0);
    toast.success(`${total} champs exportés en Excel`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="field-mapping-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
            Mapping Configuration
          </h1>
          <p className="mt-1 text-slate-500">
            Configurez les correspondances d'objets et de champs entre HubSpot et Business Central
          </p>
        </div>
        <Button 
          onClick={() => setShowObjectMappingDialog(true)}
          data-testid="add-object-mapping-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Mapping d'Objets
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Object Mappings List */}
        <div className="lg:col-span-1 space-y-4">
          <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-600" />
            Mappings d'Objets
          </h2>
          
          {objectMappings.length > 0 ? (
            <div className="space-y-2">
              {objectMappings.map((mapping) => (
                <Card 
                  key={mapping.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedObjectMapping?.id === mapping.id 
                      ? 'ring-2 ring-indigo-500 bg-indigo-50/50' 
                      : ''
                  }`}
                  onClick={() => handleSelectObjectMapping(mapping)}
                  data-testid={`object-mapping-${mapping.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 rounded-full bg-[#FF7A59]" />
                          <span className="text-sm font-medium text-slate-900 truncate">
                            {mapping.hubspot_object_label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-400">
                          <ArrowLeftRight className="w-4 h-4" />
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="w-2 h-2 rounded-full bg-[#00B7C3]" />
                          <span className="text-sm font-medium text-slate-900 truncate">
                            {mapping.bc_entity_label}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteObjectMapping(mapping.id);
                          }}
                          data-testid={`delete-object-mapping-${mapping.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-6 text-center">
                <Layers className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Aucun mapping d'objets</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowObjectMappingDialog(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un mapping
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Field Mappings */}
        <div className="lg:col-span-2">
          {selectedObjectMapping ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                  <Settings2 className="w-5 h-5 text-indigo-600" />
                  Mappings de Champs
                  <Badge variant="outline" className="ml-2">
                    {selectedObjectMapping.hubspot_object_label} ↔ {selectedObjectMapping.bc_entity_label}
                  </Badge>
                </h2>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={fieldsLoading}>
                        <Download className="w-4 h-4 mr-2" />
                        Exporter
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel className="text-xs text-slate-500">Excel (.xlsx)</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => exportExcel("hubspot")}>
                        <FileSpreadsheet className="w-4 h-4 mr-2 text-[#FF7A59]" />
                        Champs HubSpot
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportExcel("bc")}>
                        <FileSpreadsheet className="w-4 h-4 mr-2 text-[#00B7C3]" />
                        Champs Business Central
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportExcel("all")}>
                        <FileSpreadsheet className="w-4 h-4 mr-2 text-indigo-600" />
                        Tous les champs
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-slate-500">CSV</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => exportCSV(hubspotFields, "HubSpot")}>
                        <FileText className="w-4 h-4 mr-2 text-[#FF7A59]" />
                        Champs HubSpot
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportCSV(bcFields, "Business Central")}>
                        <FileText className="w-4 h-4 mr-2 text-[#00B7C3]" />
                        Champs Business Central
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshLabels}
                    disabled={refreshingLabels}
                    title="Rafraîchir les labels BC depuis l'API"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${refreshingLabels ? "animate-spin" : ""}`} />
                    Rafraîchir labels
                  </Button>
                  <Button
                    onClick={() => setShowFieldMappingDialog(true)}
                    data-testid="add-field-mapping-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un champ
                  </Button>
                </div>
              </div>

              {/* Matching Key Configuration */}
              <Card className={`border ${selectedObjectMapping.matching_key_hs && selectedObjectMapping.matching_key_bc ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-slate-50/30'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <KeyRound className={`w-4 h-4 ${selectedObjectMapping.matching_key_hs && selectedObjectMapping.matching_key_bc ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <span className="text-sm font-medium text-slate-700">Clés de correspondance</span>
                    <span className="text-xs text-slate-500 ml-1">(pour faire le lien entre les enregistrements)</span>
                    {selectedObjectMapping.matching_key_hs && selectedObjectMapping.matching_key_bc && (
                      <Badge variant="outline" className="ml-auto text-emerald-600 border-emerald-300 text-[10px]">Configuré</Badge>
                    )}
                    {(!selectedObjectMapping.matching_key_hs || !selectedObjectMapping.matching_key_bc) && (
                      <Badge variant="outline" className="ml-auto text-slate-500 border-slate-300 text-[10px]">Optionnel</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-3 items-end">
                    <div className="space-y-1">
                      <label className="text-xs text-slate-500 flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-[#FF7A59]" />
                        Champ HubSpot
                      </label>
                      <Select value={matchingKeyHs} onValueChange={setMatchingKeyHs}>
                        <SelectTrigger className="h-9 text-sm bg-white">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {[...hubspotFields].sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name, "fr")).map((f) => (
                            <SelectItem key={f.name} value={f.name}>{f.label || f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center pb-1">
                      <Link2 className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-slate-500 flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-[#00B7C3]" />
                        Champ Business Central
                      </label>
                      <Select value={matchingKeyBc} onValueChange={setMatchingKeyBc}>
                        <SelectTrigger className="h-9 text-sm bg-white">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          {[...bcFields].sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name, "fr")).map((f) => (
                            <SelectItem key={f.name} value={f.name}>{f.label || f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      size="sm"
                      className="h-9"
                      onClick={handleSaveMatchingKeys}
                      disabled={savingMatchingKeys}
                    >
                      {savingMatchingKeys ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      {savingMatchingKeys ? "" : "Sauver"}
                    </Button>
                  </div>

                  {/* Sync Mode Option */}
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <label className="text-xs font-medium text-slate-700">Mode de synchronisation</label>
                        <p className="text-[11px] text-slate-400">
                          {syncMode === "update_only"
                            ? "Mise à jour uniquement : ne crée pas de nouveaux enregistrements"
                            : "Création + mise à jour : crée les enregistrements manquants"}
                        </p>
                      </div>
                      <Select value={syncMode} onValueChange={setSyncMode}>
                        <SelectTrigger className={`w-[200px] h-8 text-xs ${syncMode === "create_and_update" ? "border-amber-400 bg-amber-50" : ""}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="update_only">Mise à jour uniquement (recommandé)</SelectItem>
                          <SelectItem value="create_and_update">Création + mise à jour</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {syncMode === "create_and_update" && (
                      <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="text-xs text-amber-800">
                          <p className="font-medium">Mode création activé</p>
                          <p className="mt-0.5">Les doublons potentiels seront automatiquement bloqués par détection de nom, mais le mode "Mise à jour uniquement" reste plus sûr.</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedObjectMapping.matching_key_hs && selectedObjectMapping.matching_key_bc ? (
                    <p className="text-xs text-slate-500 mt-3">
                      Les enregistrements seront appariés par : <strong>{selectedObjectMapping.matching_key_hs}</strong> (HubSpot) = <strong>{selectedObjectMapping.matching_key_bc}</strong> (BC).
                      {syncMode === "update_only"
                        ? " Seuls les enregistrements existants seront mis à jour."
                        : " Si aucun enregistrement correspondant n'est trouvé, il sera créé automatiquement."}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400 mt-3">
                      Sans clés de correspondance, la sync créera tous les enregistrements de la source vers la destination (mode création).
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Column Headers */}
              <div className="grid grid-cols-[1fr_auto_1fr] gap-4 px-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF7A59]" />
                  <span className="text-sm font-medium text-slate-700">
                    {selectedObjectMapping.hubspot_object_label}
                  </span>
                </div>
                <div className="w-24" />
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#00B7C3]" />
                  <span className="text-sm font-medium text-slate-700">
                    {selectedObjectMapping.bc_entity_label}
                  </span>
                </div>
              </div>

              {/* Field Mappings List */}
              <Card>
                <CardContent className="p-0">
                  {fieldsLoading ? (
                    <div className="flex items-center justify-center p-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
                    </div>
                  ) : fieldMappings.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {fieldMappings.map((mapping) => (
                        <div 
                          key={mapping.id}
                          className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center p-4 hover:bg-slate-50 transition-colors"
                          data-testid={`field-mapping-row-${mapping.id}`}
                        >
                          {/* HubSpot Field */}
                          <div className="flex items-center gap-3">
                            <div className="flex-1 px-3 py-2 rounded-md bg-[#FF7A59]/5 border border-[#FF7A59]/20">
                              <p className="text-sm font-medium text-slate-900">
                                {mapping.hubspot_field_label}
                              </p>
                              <p className="text-xs text-slate-500 font-mono">
                                {mapping.hubspot_field}
                              </p>
                            </div>
                          </div>

                          {/* Direction & Sync Mode Selectors */}
                          <div className="flex flex-col items-center gap-1">
                            <Select
                              value={mapping.sync_direction}
                              onValueChange={(value) => handleDirectionChange(mapping, value)}
                            >
                              <SelectTrigger className="w-40" data-testid={`direction-select-${mapping.id}`}>
                                <SelectValue>
                                  <div className="flex items-center gap-2">
                                    <DirectionIcon direction={mapping.sync_direction} className="w-4 h-4" />
                                    <span className="text-xs">
                                      {directionOptions.find(d => d.value === mapping.sync_direction)?.label}
                                    </span>
                                  </div>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {directionOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    <div className="flex items-center gap-2">
                                      <option.icon className="w-4 h-4" />
                                      {option.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={mapping.sync_mode || "inherit"}
                              onValueChange={(value) => handleSyncModeChange(mapping, value)}
                            >
                              <SelectTrigger className={`w-40 h-7 text-[11px] ${
                                (mapping.sync_mode || "inherit") !== "inherit"
                                  ? "border-amber-400 bg-amber-50"
                                  : "border-slate-200"
                              }`} data-testid={`sync-mode-select-${mapping.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="inherit">
                                  <span className="text-xs">Hériter de l'objet</span>
                                </SelectItem>
                                <SelectItem value="update_only">
                                  <span className="text-xs">Mise à jour uniquement</span>
                                </SelectItem>
                                <SelectItem value="create_and_update">
                                  <span className="text-xs">Création + mise à jour</span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* BC Field */}
                          <div className="flex items-center gap-3">
                            <div className="flex-1 px-3 py-2 rounded-md bg-[#00B7C3]/5 border border-[#00B7C3]/20">
                              <p className="text-sm font-medium text-slate-900">
                                {mapping.bc_field_label}
                              </p>
                              <p className="text-xs text-slate-500 font-mono">
                                {mapping.bc_field}
                              </p>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={mapping.is_active}
                                onCheckedChange={() => handleToggleFieldActive(mapping)}
                                data-testid={`toggle-${mapping.id}`}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                                onClick={() => handleDeleteFieldMapping(mapping.id)}
                                data-testid={`delete-${mapping.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500">Aucun mapping de champ configuré</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => setShowFieldMappingDialog(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter un champ
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="h-full">
              <CardContent className="flex flex-col items-center justify-center h-64 text-center">
                <Settings2 className="w-16 h-16 text-slate-200 mb-4" />
                <p className="text-slate-500 font-medium">Sélectionnez un mapping d'objets</p>
                <p className="text-sm text-slate-400 mt-1">
                  Choisissez un mapping d'objets à gauche pour configurer les champs
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Object Mapping Dialog */}
      <Dialog open={showObjectMappingDialog} onOpenChange={setShowObjectMappingDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouveau Mapping d'Objets</DialogTitle>
            <DialogDescription>
              Sélectionnez un objet HubSpot et une entité Business Central à mapper
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-[1fr_auto_1fr] gap-6 py-6">
            {/* HubSpot Object Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-[#FF7A59]" />
                <span className="text-sm font-medium">Objet HubSpot</span>
              </div>
              <Select
                value={newObjectMapping.hubspot_object}
                onValueChange={(value) => {
                  const obj = hubspotObjects.find(o => o.name === value);
                  setNewObjectMapping({ 
                    ...newObjectMapping, 
                    hubspot_object: value,
                    hubspot_object_label: obj?.label || value
                  });
                }}
              >
                <SelectTrigger data-testid="select-hubspot-object">
                  <SelectValue placeholder="Sélectionner un objet" />
                </SelectTrigger>
                <SelectContent>
                  {hubspotObjects.map((obj) => (
                    <SelectItem key={obj.name} value={obj.name}>
                      <div>
                        <p className="font-medium">{obj.label}</p>
                        {obj.description && (
                          <p className="text-xs text-slate-500">{obj.description}</p>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Arrow */}
            <div className="flex items-center justify-center">
              <ArrowLeftRight className="w-6 h-6 text-slate-400" />
            </div>

            {/* BC Entity Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-[#00B7C3]" />
                <span className="text-sm font-medium">Entité Business Central</span>
              </div>
              <Select
                value={newObjectMapping.bc_entity}
                onValueChange={(value) => {
                  const entity = bcEntities.find(e => e.name === value);
                  setNewObjectMapping({ 
                    ...newObjectMapping, 
                    bc_entity: value,
                    bc_entity_label: entity?.label || value
                  });
                }}
              >
                <SelectTrigger data-testid="select-bc-entity">
                  <SelectValue placeholder="Sélectionner une entité" />
                </SelectTrigger>
                <SelectContent>
                  {bcEntities.map((entity) => (
                    <SelectItem key={entity.name} value={entity.name}>
                      <div>
                        <p className="font-medium">{entity.label}</p>
                        {entity.description && (
                          <p className="text-xs text-slate-500">{entity.description}</p>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowObjectMappingDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateObjectMapping} data-testid="save-object-mapping-btn">
              <Save className="w-4 h-4 mr-2" />
              Créer le mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field Mapping Dialog */}
      <Dialog open={showFieldMappingDialog} onOpenChange={setShowFieldMappingDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Nouveau Mapping de Champ</DialogTitle>
            <DialogDescription>
              Mappez un champ {selectedObjectMapping?.hubspot_object_label} vers {selectedObjectMapping?.bc_entity_label}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* HubSpot Field Selection */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF7A59]" />
                <span className="text-sm font-medium text-slate-700">Champ HubSpot</span>
                <Badge variant="outline" className="ml-auto text-[10px] text-slate-400">
                  {hubspotFields.length} champs
                </Badge>
              </div>
              <Popover open={hsComboboxOpen} onOpenChange={setHsComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={hsComboboxOpen}
                    className="w-full justify-between font-normal h-10"
                    data-testid="select-hubspot-field"
                  >
                    {newFieldMapping.hubspot_field ? (
                      <div className="flex items-center gap-2 truncate">
                        <span className="font-medium truncate">
                          {hubspotFields.find(f => f.name === newFieldMapping.hubspot_field)?.label || newFieldMapping.hubspot_field}
                        </span>
                        <span className="text-xs text-slate-400 font-mono truncate">
                          {newFieldMapping.hubspot_field}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Rechercher un champ HubSpot...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Rechercher par nom ou label..." />
                    <CommandScrollList>
                      {fieldsLoading ? (
                        <div className="p-4 text-center">
                          <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>
                            <p className="text-sm text-slate-500">Aucun champ trouvé.</p>
                          </CommandEmpty>
                          <CommandGroup>
                            {[...hubspotFields].sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name, "fr")).map((field) => (
                              <CommandItem
                                key={field.name}
                                value={`${field.name} ${field.label}`}
                                onSelect={() => {
                                  setNewFieldMapping({ ...newFieldMapping, hubspot_field: field.name });
                                  setHsComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4 shrink-0",
                                    newFieldMapping.hubspot_field === field.name ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{field.label}</p>
                                  <p className="text-xs text-slate-500 font-mono truncate">{field.name}</p>
                                </div>
                                {field.type && field.type !== "string" && (
                                  <Badge variant="outline" className="ml-2 text-[10px] shrink-0">
                                    {field.type}
                                  </Badge>
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandScrollList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Direction */}
            <div className="flex items-center gap-3 px-1">
              <div className="flex-1 h-px bg-slate-200" />
              <Select
                value={newFieldMapping.sync_direction}
                onValueChange={(value) => setNewFieldMapping({ ...newFieldMapping, sync_direction: value })}
              >
                <SelectTrigger className="w-44 h-9" data-testid="select-direction">
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <DirectionIcon direction={newFieldMapping.sync_direction} className="w-4 h-4" />
                      <span className="text-xs">{directionOptions.find(d => d.value === newFieldMapping.sync_direction)?.label}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {directionOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <option.icon className="w-4 h-4" />
                        <span className="text-sm">{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* BC Field Selection with Tabs */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-[#00B7C3]" />
                <span className="text-sm font-medium text-slate-700">Champ Business Central</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-6 text-xs gap-1 px-2"
                  onClick={() => {
                    setBcCustomMode(!bcCustomMode);
                    setNewFieldMapping({ ...newFieldMapping, bc_field: "", bc_field_label: "" });
                  }}
                  data-testid="toggle-bc-custom-mode"
                >
                  <PenLine className="w-3 h-3" />
                  {bcCustomMode ? "Rechercher" : "Custom"}
                </Button>
              </div>

              {bcFieldsError && !bcCustomMode && (
                <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{bcFieldsError}</span>
                </div>
              )}

              {bcCustomMode ? (
                <div className="space-y-2">
                  <Input
                    placeholder="Nom technique du champ (ex: myCustomField)"
                    value={newFieldMapping.bc_field}
                    onChange={(e) => setNewFieldMapping({
                      ...newFieldMapping,
                      bc_field: e.target.value,
                      bc_field_label: ""
                    })}
                    data-testid="input-bc-custom-field"
                    className="font-mono text-sm h-10"
                  />
                  <Input
                    placeholder="Label (optionnel)"
                    value={newFieldMapping.bc_field_label}
                    onChange={(e) => setNewFieldMapping({
                      ...newFieldMapping,
                      bc_field_label: e.target.value
                    })}
                    data-testid="input-bc-custom-label"
                    className="text-sm h-10"
                  />
                  <p className="text-xs text-slate-400">
                    Saisissez le nom technique exact du champ BC custom tel qu'il apparait dans l'API.
                  </p>
                </div>
              ) : (
                <Tabs value={bcFieldTab} onValueChange={setBcFieldTab} className="w-full">
                  <TabsList className="flex w-full h-9 p-1 bg-muted rounded-lg">
                    <TabsTrigger value="effective" className="flex-1 text-xs">
                      Mapping effectif
                      {odataFields?.has_odata_support && (
                        <Badge variant="secondary" className="ml-1.5 text-[9px] px-1 py-0 h-4">
                          {odataFields.fields.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="all" className="flex-1 text-xs">
                      Tous les champs
                      <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0 h-4">
                        {bcFields.length}
                      </Badge>
                    </TabsTrigger>
                  </TabsList>

                  {/* Mapping Effectif Tab */}
                  <TabsContent value="effective" className="mt-2">
                    {!odataFields?.has_odata_support ? (
                      <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-center">
                        <p className="text-sm text-slate-600">
                          Pas d'action OData configurée pour cette entité.
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          La sync utilisera l'API standard. Utilisez l'onglet "Tous les champs".
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="mb-2 p-2 rounded-md bg-emerald-50 border border-emerald-200">
                          <p className="text-xs text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 inline mr-1" />
                            {odataFields.message}
                          </p>
                        </div>
                        <Popover open={bcComboboxOpen} onOpenChange={setBcComboboxOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={bcComboboxOpen}
                              className="w-full justify-between font-normal h-10"
                              data-testid="select-bc-field-effective"
                            >
                              {newFieldMapping.bc_field ? (
                                <div className="flex items-center gap-2 truncate">
                                  <span className="font-medium truncate">
                                    {odataFields.fields.find(f => f.name === newFieldMapping.bc_field)?.label || newFieldMapping.bc_field}
                                  </span>
                                  <span className="text-xs text-slate-400 font-mono truncate">
                                    {newFieldMapping.bc_field}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Sélectionner un champ OData...</span>
                              )}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Rechercher..." />
                              <CommandScrollList>
                                <CommandEmpty>Aucun champ trouvé.</CommandEmpty>
                                <CommandGroup>
                                  {odataFields.fields.map((field) => (
                                    <CommandItem
                                      key={field.name}
                                      value={`${field.name} ${field.label}`}
                                      onSelect={() => {
                                        setNewFieldMapping({ ...newFieldMapping, bc_field: field.name, bc_field_label: field.label });
                                        setBcComboboxOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4 shrink-0",
                                          newFieldMapping.bc_field === field.name ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{field.label}</p>
                                        <p className="text-xs text-slate-500 font-mono truncate">{field.name}</p>
                                        {field.description && (
                                          <p className="text-[10px] text-slate-400 truncate">{field.description}</p>
                                        )}
                                      </div>
                                      <Badge variant="secondary" className="ml-2 text-[9px] shrink-0">
                                        requis
                                      </Badge>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandScrollList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </>
                    )}
                  </TabsContent>

                  {/* Tous les champs Tab */}
                  <TabsContent value="all" className="mt-2">
                    <Popover open={bcComboboxOpen} onOpenChange={setBcComboboxOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={bcComboboxOpen}
                          className="w-full justify-between font-normal h-10"
                          data-testid="select-bc-field"
                        >
                          {newFieldMapping.bc_field ? (
                            <div className="flex items-center gap-2 truncate">
                              <span className="font-medium truncate">
                                {bcFields.find(f => f.name === newFieldMapping.bc_field)?.label || newFieldMapping.bc_field}
                              </span>
                              <span className="text-xs text-slate-400 font-mono truncate">
                                {newFieldMapping.bc_field}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">Rechercher un champ BC...</span>
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Rechercher par nom ou label..." />
                          <CommandScrollList>
                            {fieldsLoading ? (
                              <div className="p-4 text-center">
                                <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                              </div>
                            ) : (
                              <>
                                <CommandEmpty>
                                  <div className="text-sm text-slate-500">
                                    Aucun champ trouvé.
                                    <button
                                      className="block mx-auto mt-2 text-indigo-600 hover:underline text-xs"
                                      onClick={() => {
                                        setBcCustomMode(true);
                                        setBcComboboxOpen(false);
                                      }}
                                    >
                                      Saisir manuellement un champ custom
                                    </button>
                                  </div>
                                </CommandEmpty>
                                <CommandGroup>
                                  {[...bcFields].sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name, "fr")).map((field) => (
                                    <CommandItem
                                      key={field.name}
                                      value={`${field.name} ${field.label}`}
                                      onSelect={() => {
                                        setNewFieldMapping({ ...newFieldMapping, bc_field: field.name });
                                        setBcComboboxOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4 shrink-0",
                                          newFieldMapping.bc_field === field.name ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{field.label}</p>
                                        <p className="text-xs text-slate-500 font-mono truncate">{field.name}</p>
                                      </div>
                                      {field.type && field.type !== "string" && (
                                        <Badge variant="outline" className="ml-2 text-[10px] shrink-0">
                                          {field.type}
                                        </Badge>
                                      )}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </>
                            )}
                          </CommandScrollList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {odataFields?.has_odata_support && (
                      <p className="text-[10px] text-amber-600 mt-1.5">
                        <AlertCircle className="w-3 h-3 inline mr-0.5" />
                        Attention: seuls les champs de l'onglet "Mapping effectif" seront utilisés par l'action OData.
                      </p>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowFieldMappingDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateFieldMapping}
              disabled={!newFieldMapping.hubspot_field || !newFieldMapping.bc_field}
              data-testid="save-field-mapping-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Créer le mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
